import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, from, of } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { ADPResourceService, ResourcesLoaderService } from 'digital-core';
import { AppConstants } from 'src/app/core/constants';
import { ChatHandler, dashboardRouteWithSlash, PGHAppLoadService } from 'src/app/core/core';
import { LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { PlanRenewalService } from 'src/app/shared/components/plan-renewal-notice';
import { PlanDetail } from 'src/app/model/plan/plan-detail';

/** Max time to wait for the selected plan detail to load before failing closed. */
const PLAN_DETAIL_TIMEOUT_MS = 10000;

/** CMS kill-switch for the direct-access block below — see the guard doc comment. */
const BLOCK_DIRECT_ACCESS_SETTING_KEY = 'PlanRenewal_BlockDirectAccess_Enabled';

/**
 * Blocks the /renew route unless:
 *   1. it was reached via an in-app "Renew" click (router `state` flag — deliberately NOT
 *      part of the URL, so a direct link, bookmark, or hard refresh never carries it), AND
 *   2. the AFX renewal offer is available for the user's selected plan.
 *
 * Check 1 is gated behind the `PlanRenewal_BlockDirectAccess_Enabled` portal setting so it can
 * be turned off from CMS without a deploy (e.g. to debug, or if a future entry point needs to
 * deep-link in without the nav-state flag). Missing/unpushed on a client defaults to enabled
 * (fail secure) — only an explicit `'false'` value disables the check.
 *
 * Functional guard (not root-provided) so it runs in the route's injection context and
 * can resolve the route-scoped `PlanRenewalService` (listed in the route's `providers`),
 * which owns `canEnterRenewalPortal` (true when either a new AFX offer is available, or
 * the user already accepted one and is coming back to view it — see the computed's doc
 * comment on `PlanRenewalService` for why `isRenewPlanEnabled()` alone isn't sufficient).
 * Mirrors the cold-load bootstrap in `ServiceRequestActivateGuard` since canActivate runs
 * before the route resolvers populate site resources / plan detail.
 */
export const PlanRenewalActivateGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const renewal = inject(PlanRenewalService);
  const appLoadService = inject(PGHAppLoadService);
  const resourcesLoader = inject(ResourcesLoaderService);
  const resourceService = inject(ADPResourceService);
  const loggedInUserSettings = inject(LoggedInUserSettings);
  const router = inject(Router);
  const chatHandler = inject(ChatHandler);

  // Portal settings live in global resources, so resolve those first — reading the CMS
  // flag before this would default to "enabled" on every cold load regardless of its
  // actual configured value.
  await ensureSiteResourcesLoaded(appLoadService, resourcesLoader, resourceService);

  const isDirectAccessGuardEnabled = resourceService.getPortalSetting(BLOCK_DIRECT_ACCESS_SETTING_KEY)?.Value !== 'false';
  const navigationState = router.getCurrentNavigation()?.extras?.state as Record<string, unknown> | undefined;
  const cameFromRenewClick = navigationState?.[AppConstants.ROUTE_NAV_STATE.PlanRenewalFromRenewClick] === true;

  if (isDirectAccessGuardEnabled && !cameFromRenewClick) {
    return router.createUrlTree([dashboardRouteWithSlash]);
  }

  await waitForPlanDetail(loggedInUserSettings);

  if (renewal.canEnterRenewalPortal()) {
    chatHandler.hideAsappChat();
    chatHandler.hideTechSupportChat();
    return true;
  }

  return router.createUrlTree([dashboardRouteWithSlash]);
};

/**
 * canActivate runs BEFORE the route resolvers (SiteConfigResolver / PageResourcesResolver),
 * so on a cold load / refresh the global settings + labels aren't populated yet — isRenewPlanEnabled
 * would read null. Drive the load ourselves, mirroring the resolvers. Fails open (lets the load
 * attempt continue) so a load error never throws inside the guard — the plan-detail wait below
 * still bounds the total time.
 */
async function ensureSiteResourcesLoaded(appLoadService: PGHAppLoadService, resourcesLoader: ResourcesLoaderService, resourceService: ADPResourceService): Promise<void> {
  if (appLoadService.isSiteResourcesLoaded.getValue()) {
    return;
  }
  try {
    await firstValueFrom(from(window['__bootstrapConfigPromise'] ?? Promise.resolve()).pipe(switchMap(() => appLoadService.loadSiteResources())));
    const pageResources = await firstValueFrom(resourcesLoader.loadAllPageResources('Global', false, true));
    resourceService.setGlobalResources(pageResources.resources);
    resourceService.setGlobalLabels(pageResources.labels);
    resourceService.setPortalPageList(pageResources.lists);
    appLoadService.isSiteResourcesLoaded.next(true);
  } catch {
    // Fail-open: if resources can't be loaded, let the plan-detail wait (and its own
    // timeout) decide whether to admit or redirect.
  }
}

/**
 * The selected plan detail loads asynchronously. Wait for the first non-null emission,
 * bounded by a timeout. Times out to null rather than throwing so isRenewPlanEnabled()
 * simply evaluates false (redirect to dashboard) instead of blocking navigation forever.
 */
async function waitForPlanDetail(loggedInUserSettings: LoggedInUserSettings): Promise<PlanDetail | null> {
  const current = loggedInUserSettings.$selectedPlanDetail.getValue();
  if (current) {
    return current;
  }
  return firstValueFrom(
    loggedInUserSettings.$selectedPlanDetail.pipe(
      filter((detail): detail is PlanDetail => detail !== null),
      take(1),
      timeout(PLAN_DETAIL_TIMEOUT_MS),
      catchError(() => of(null))
    )
  );
}
